// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { PageContent } from '@/components/page-content';
import {
  DownloadRounded,
  SettingsRounded,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { AssetsInventory } from '@/inventory/api/types';
import { InventoryOverview } from '@/inventory/components/overview/inventory-overview';
import { InventoryOverviewSkeleton } from '@/inventory/components/overview/inventory-overview-skeleton';
import { exportInventoryOverviewCSV } from '@/inventory/components/overview/utils/export-inventory-csv';
import { transformInventoryData } from '@/inventory/components/overview/utils/transform-inventory-data';
import { useInventory } from '@/inventory/hooks';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popover,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useRef } from 'react';
import { z } from 'zod';

/**
 * Schema for dashboard filter settings
 */
const dashboardFiltersSchema = z.object({
  includeScrappedAssets: z.boolean().default(false),
  hideZeroes: z.boolean().default(false),
  includeArchivedProducts: z.boolean().default(false),
  excludeArchivedLocations: z.boolean().default(false),
});

type DashboardFilters = z.infer<typeof dashboardFiltersSchema>;

/**
 * Route configuration for the asset dashboard page.
 * This route displays aggregated metrics and charts for all assets.
 */
export const Route = createFileRoute('/_app/dashboard/asset')({
  component: AssetDashboardRoute,
});

/**
 * Asset dashboard page component.
 *
 * Features:
 * - Asset overview and analytics dashboard
 * - Displays key asset metrics and performance indicators
 * - Provides comprehensive asset reporting capabilities
 * - CSV export functionality for inventory data
 *
 * Layout:
 * - Responsive grid layout adapting to screen size
 * - Charts and widgets arranged for optimal data visualization
 * - Interactive components for asset analysis
 */
export function AssetDashboardRoute() {
  // Persistent dashboard filter settings
  const [dashboardFilters, setDashboardFilters] = useLocalStorage(
    'asset-dashboard-filters',
    dashboardFiltersSchema
  );

  const {
    includeScrappedAssets = false,
    includeArchivedProducts = false,
    excludeArchivedLocations = false,
    hideZeroes = false,
  } = dashboardFilters || {};

  // Helper functions to update individual filter settings
  const updateFilter = (key: keyof DashboardFilters, value: boolean) => {
    const newFilters: DashboardFilters = {
      includeScrappedAssets: false,
      includeArchivedProducts: false,
      excludeArchivedLocations: false,
      hideZeroes: false,
      ...dashboardFilters,
      [key]: value,
    };
    setDashboardFilters(newFilters);
  };

  // Settings menu disclosure and ref
  const settingsMenu = useDisclosure();
  const settingsButtonRef = useRef<HTMLButtonElement>(null);

  // Persistent collapsed state for inventory sections
  const [isInventoryExpanded, setIsInventoryExpanded] = useLocalStorage(
    'asset-dashboard-inventory-expanded',
    z.boolean().default(true)
  );
  const [isNonInventoryExpanded, setIsNonInventoryExpanded] = useLocalStorage(
    'asset-dashboard-non-inventory-expanded',
    z.boolean().default(false)
  );
  const [isInTransitExpanded, setIsInTransitExpanded] = useLocalStorage(
    'asset-dashboard-in-transit-expanded',
    z.boolean().default(true)
  );

  // The single inventory fetch for the page; the overview renders from this
  const { inventory: inventoryResponse, isLoading } = useInventory({
    type: 'assets',
    dimension: 'item',
    enabled: true,
    filters: {
      includeNonInventory: true,
      includeInTransit: true,
      includeScrappedAssets,
      includeArchivedProducts,
      excludeArchivedLocations,
      // The dashboard and its CSV export are count-only
      excludeSerials: true,
    },
  });

  const assetsInventory = inventoryResponse?.data as
    | AssetsInventory
    | undefined;

  const overviewData = useMemo(
    () =>
      assetsInventory ? transformInventoryData(assetsInventory) : undefined,
    [assetsInventory]
  );

  const handleExportCSV = () => {
    if (overviewData) {
      exportInventoryOverviewCSV(overviewData, includeScrappedAssets);
    }
  };

  return (
    <PageContent
      sx={{
        gap: 2,
        flex: 1,
        display: 'grid',
        maxHeight: '100%',
        gridTemplateRows: 'auto 1fr',
      }}
    >
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        {/* Header Section */}
        <Stack
          spacing={1}
          padding={2}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h6">Asset dashboard</Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<DownloadRounded />}
              onClick={handleExportCSV}
              disabled={!inventoryResponse?.data || isLoading}
            >
              Export CSV
            </Button>

            <IconButton
              aria-label="Dashboard settings"
              ref={settingsButtonRef}
              onClick={settingsMenu.open}
            >
              <Badge
                variant="dot"
                color="error"
                invisible={
                  !includeScrappedAssets &&
                  !includeArchivedProducts &&
                  !excludeArchivedLocations &&
                  !hideZeroes
                }
              >
                <SettingsRounded fontSize="small" />
              </Badge>
            </IconButton>
          </Box>
        </Stack>

        <Divider />

        {isLoading || !assetsInventory || !overviewData ? (
          <InventoryOverviewSkeleton />
        ) : assetsInventory.results.length === 0 ? (
          <TableContainer>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell align="center">
                    <Typography variant="subtitle1">No assets found</Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <InventoryOverview
            data={overviewData}
            showScrappedAssets={includeScrappedAssets}
            hideZeroes={hideZeroes}
            isInventoryExpanded={isInventoryExpanded ?? true}
            isNonInventoryExpanded={isNonInventoryExpanded ?? false}
            isInTransitExpanded={isInTransitExpanded ?? true}
            onToggleInventory={() =>
              setIsInventoryExpanded(!(isInventoryExpanded ?? true))
            }
            onToggleNonInventory={() =>
              setIsNonInventoryExpanded(!(isNonInventoryExpanded ?? false))
            }
            onToggleInTransit={() =>
              setIsInTransitExpanded(!(isInTransitExpanded ?? true))
            }
          />
        )}
      </Paper>

      <Popover
        anchorEl={settingsButtonRef.current}
        open={settingsMenu.isOpen}
        onClose={settingsMenu.close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <List dense>
          <ListItemButton
            onClick={() =>
              updateFilter('includeScrappedAssets', !includeScrappedAssets)
            }
          >
            <ListItemText>
              <Typography variant="body2">Show scrapped status</Typography>
            </ListItemText>
            <Switch
              edge="end"
              size="small"
              checked={includeScrappedAssets}
              onChange={() =>
                updateFilter('includeScrappedAssets', !includeScrappedAssets)
              }
            />
          </ListItemButton>

          <ListItemButton
            onClick={() => updateFilter('hideZeroes', !hideZeroes)}
          >
            <ListItemText>
              <Typography variant="body2">
                Hide &lsquo;zero&rsquo; statuses
              </Typography>
            </ListItemText>
            <Switch
              edge="end"
              size="small"
              checked={hideZeroes}
              onChange={() => updateFilter('hideZeroes', !hideZeroes)}
            />
          </ListItemButton>

          <ListItemButton
            onClick={() =>
              updateFilter('includeArchivedProducts', !includeArchivedProducts)
            }
          >
            <ListItemText>
              <Typography variant="body2">Show archived products</Typography>
            </ListItemText>
            <Switch
              edge="end"
              size="small"
              checked={includeArchivedProducts}
              onChange={() =>
                updateFilter(
                  'includeArchivedProducts',
                  !includeArchivedProducts
                )
              }
            />
          </ListItemButton>

          <ListItemButton
            onClick={() =>
              updateFilter(
                'excludeArchivedLocations',
                !excludeArchivedLocations
              )
            }
          >
            <ListItemText>
              <Typography variant="body2">Hide archived locations</Typography>
            </ListItemText>
            <Switch
              edge="end"
              size="small"
              checked={excludeArchivedLocations}
              onChange={() =>
                updateFilter(
                  'excludeArchivedLocations',
                  !excludeArchivedLocations
                )
              }
            />
          </ListItemButton>
        </List>
      </Popover>
    </PageContent>
  );
}
